// Column definitions for the Photoshoots table (PX-386). A factory because the
// cells need live handlers (quick-edit toggles, plan chips, open editor) +
// the plans catalog. Groupable columns carry a stable `id` (matching GROUP_DIMS)
// and a `getGroupingValue` reading the exploded `_g_*` field for array dims.
import type { ColumnDef } from '@tanstack/react-table'
import { Toggle } from '@/components/FormControls'
import type { AdminPhotoshootResponse, AdminPhotoshootUpdate, AdminPlanResponse } from '@/types/api'
import { PlanChips } from './PlanChips'
import { effectiveModels, type PhotoshootRow } from './model'

export interface ColumnCtx {
  plans: AdminPlanResponse[]
  patching: boolean
  onPatch: (id: string, body: AdminPhotoshootUpdate) => void
  onOpen: (ps: AdminPhotoshootResponse) => void
}

// Human label per column id — drives the Columns tool-panel + group headers.
export const COLUMN_LABELS: Record<string, string> = {
  cover: 'Cover',
  name: 'Name',
  category: 'Category',
  tags: 'Tags',
  model: 'Model',
  plan: 'Plans',
  cost: 'Cost',
  status: 'Status',
  kind: 'Destination',
  active: 'Active',
  trending: 'Trending',
  aspect: 'Formats',
  sort: 'Sort',
  created: 'Created',
}

export function buildColumns(ctx: ColumnCtx): ColumnDef<PhotoshootRow>[] {
  return [
    {
      id: 'cover',
      header: '',
      enableGrouping: false,
      enableSorting: false,
      cell: ({ row }) => (
        <img
          src={row.original.cover_image_url}
          alt=""
          // max-w-none overrides the Tailwind-preflight `img { max-width:100% }`
          // so a narrow cell can never clamp the 36×36 square into a sliver.
          className="h-9 w-9 max-w-none shrink-0 rounded-md object-cover"
          loading="lazy"
        />
      ),
    },
    {
      id: 'name',
      header: 'Name',
      accessorFn: (r) => r.name,
      enableGrouping: false,
      cell: ({ row }) => (
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate font-medium text-ink-900">{row.original.name}</span>
          <span className="shrink-0 rounded bg-ink-50 px-1.5 py-0.5 font-mono text-[10px] text-ink-400">
            {row.original.slug}
          </span>
        </div>
      ),
    },
    {
      id: 'category',
      header: 'Category',
      accessorFn: (r) => r.category ?? '',
      getGroupingValue: (r) => r.category ?? '',
      enableGrouping: true,
      cell: ({ row }) =>
        row.original.category ? (
          <span className="rounded bg-ink-100 px-1.5 py-0.5 text-xs text-ink-700">{row.original.category}</span>
        ) : (
          <span className="text-xs text-ink-300">—</span>
        ),
    },
    {
      id: 'tags',
      header: 'Tags',
      accessorFn: (r) => r._g_tags ?? '',
      getGroupingValue: (r) => r._g_tags ?? '',
      enableGrouping: true,
      enableSorting: false,
      cell: ({ row }) =>
        row.original.tags.length ? (
          <div className="flex flex-wrap gap-1">
            {row.original.tags.map((t) => (
              <span key={t} className="rounded bg-ink-50 px-1.5 py-0.5 text-[11px] text-ink-600">{t}</span>
            ))}
          </div>
        ) : (
          <span className="text-xs text-ink-300">—</span>
        ),
    },
    {
      id: 'model',
      header: 'Model',
      // The REAL models (variants, else the default as fallback) — PX-386. A
      // photoshoot can offer several, so this is a multi-membership dim like
      // tags/plan: grouping value is the exploded `_g_model`.
      accessorFn: (r) => r._g_model ?? '',
      getGroupingValue: (r) => r._g_model ?? '',
      enableGrouping: true,
      enableSorting: false,
      cell: ({ row }) => {
        const models = effectiveModels(row.original)
        return (
          <div className="flex min-w-0 flex-col gap-0.5">
            {models.map((m) => (
              <span key={m.slug} className="truncate text-xs text-ink-700">{m.label}</span>
            ))}
          </div>
        )
      },
    },
    {
      id: 'plan',
      header: 'Plans',
      accessorFn: (r) => r._g_plan ?? '',
      getGroupingValue: (r) => r._g_plan ?? '',
      enableGrouping: true,
      enableSorting: false,
      cell: ({ row }) => (
        <PlanChips
          compact
          plans={ctx.plans}
          selectedIds={row.original.allowed_plan_ids}
          disabled={ctx.patching}
          onToggle={(planId) => {
            const ids = row.original.allowed_plan_ids
            const next = ids.includes(planId)
              ? ids.filter((p) => p !== planId)
              : [...ids, planId].sort((a, b) => a - b)
            ctx.onPatch(row.original.id, { allowed_plan_ids: next })
          }}
        />
      ),
    },
    {
      id: 'cost',
      // Cost per EFFECTIVE model (PX-386) — one line per model, aligned with the
      // Model cell. Sorts by the first effective model's cost. Previously read the
      // vestigial default model_credit_cost, so it stayed Nano Banana's price.
      header: 'Cost',
      accessorFn: (r) => effectiveModels(r)[0]?.cost ?? r.model_credit_cost,
      enableGrouping: false,
      cell: ({ row }) => (
        <div className="flex flex-col gap-0.5">
          {effectiveModels(row.original).map((m) => (
            <span key={m.slug} className="text-xs text-ink-600">✦{m.cost}</span>
          ))}
        </div>
      ),
    },
    {
      // PX-435: lifecycle status. Changing it syncs is_active server-side
      // (approved → shown in app, pending/archived → hidden) — the inline
      // "move between tables" affordance.
      id: 'status',
      header: 'Status',
      accessorFn: (r) => r.status,
      getGroupingValue: (r) => r.status,
      enableGrouping: true,
      cell: ({ row }) => (
        <select
          value={row.original.status}
          disabled={ctx.patching}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) =>
            ctx.onPatch(row.original.id, {
              status: e.target.value as 'pending' | 'approved' | 'archived',
            })
          }
          className="rounded-md border border-ink-200 px-2 py-1 text-xs focus:border-brand-500 focus:outline-none"
        >
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="archived">Archived</option>
        </select>
      ),
    },
    {
      // PX-437: destination tab (style / upscale) — inline "move between tabs"
      // affordance, like Status. Independent of status (doesn't gate visibility).
      id: 'kind',
      header: 'Destination',
      accessorFn: (r) => r.kind,
      getGroupingValue: (r) => r.kind,
      enableGrouping: true,
      cell: ({ row }) => (
        <select
          value={row.original.kind}
          disabled={ctx.patching}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) =>
            ctx.onPatch(row.original.id, {
              kind: e.target.value as 'style' | 'upscale',
            })
          }
          className="rounded-md border border-ink-200 px-2 py-1 text-xs focus:border-brand-500 focus:outline-none"
        >
          <option value="style">Styles</option>
          <option value="upscale">Upscale</option>
        </select>
      ),
    },
    {
      id: 'active',
      header: 'Active',
      accessorFn: (r) => r.is_active,
      getGroupingValue: (r) => String(r.is_active),
      enableGrouping: true,
      cell: ({ row }) => (
        <span onClick={(e) => e.stopPropagation()} className="inline-flex">
          <Toggle
            checked={row.original.is_active}
            disabled={ctx.patching}
            onChange={(v) => ctx.onPatch(row.original.id, { is_active: v })}
          />
        </span>
      ),
    },
    {
      id: 'trending',
      header: 'Trending',
      accessorFn: (r) => r.is_trending_pinned,
      getGroupingValue: (r) => String(r.is_trending_pinned),
      enableGrouping: true,
      cell: ({ row }) => (
        <span onClick={(e) => e.stopPropagation()} className="inline-flex">
          <Toggle
            checked={row.original.is_trending_pinned}
            disabled={ctx.patching}
            onChange={(v) => ctx.onPatch(row.original.id, { is_trending_pinned: v })}
          />
        </span>
      ),
    },
    {
      id: 'aspect',
      header: 'Formats',
      enableGrouping: false,
      enableSorting: false,
      cell: ({ row }) =>
        row.original.allowed_aspect_ratios.length ? (
          <span className="font-mono text-[10px] text-ink-500">{row.original.allowed_aspect_ratios.join(' ')}</span>
        ) : (
          <span className="text-xs text-ink-300">all</span>
        ),
    },
    {
      id: 'sort',
      header: 'Sort',
      accessorFn: (r) => r.sort_order,
      enableGrouping: false,
      cell: ({ row }) => <span className="text-xs text-ink-500">{row.original.sort_order}</span>,
    },
    {
      id: 'created',
      header: 'Created',
      accessorFn: (r) => r.created_at,
      enableGrouping: false,
      cell: ({ row }) => (
        <span className="text-xs text-ink-400">{new Date(row.original.created_at).toLocaleDateString()}</span>
      ),
    },
  ]
}
